import React, { useEffect } from "react";
import { RouteComponentProps } from "react-router";
import { useForm } from "react-hook-form";

import useInsertMad, { MadData } from "../graphql/hooks/mutations/useInsertMad";

import {
  EmptyState,
  Link,
  ScreenContainer,
  ScreenHeader,
  Button,
} from "../components/wombat-ui";

import { MadIcon } from "../components/icons";

import useGetMadFull from "../graphql/hooks/queries/useGetMadFull";
import { InsertMadMutationVariables } from "../graphql/types";
import { MadFullFragment } from "../graphql/fragments";

import useTranslations from "../hooks/useTranslations";
import useAddNotification from "../hooks/useAddNotification";

import MadConfigurationForm from "../components/mads/StepForm/MadConfigurationForm";

type MissionFormVariables = Omit<
  InsertMadMutationVariables["input"],
  "talentId"
> & { talentIds: string[] };

type EditMadScreenFormProps = { mad: MadFullFragment }

const EditMadScreenForm: React.FC<EditMadScreenFormProps> = ({ mad }) => {
  const t = useTranslations();

  const [addNotification] = useAddNotification();

  const {
    register,
    handleSubmit,
    reset,
    ...useFormMethods
  } = useForm<MissionFormVariables>({
    defaultValues: {
      madSupervisorsAttributes: mad.madSupervisors.map(
        ({ user, emailNotificationsEnabled }) => ({
          userId: user.id,
          emailNotificationsEnabled,
        })
      ),
      ...mad,
    },
  });

  const [insertMad] = useInsertMad();

  const onSubmit = (input) => {
    insertMad({ variables: { input } }).then(() => {
      addNotification({
        variables: {
          title: t("userProfile.notification.success") as string,
          status: "success",
        },
      });
    });
  };

  return (
    <form id="editMadForm" onSubmit={handleSubmit(onSubmit)}>
      <ScreenHeader
        icon={MadIcon}
        title={t("mads.updateMad", { title: mad.title ? mad.title : mad.template?.title })}
        actions={[
          <Button
            key="submit"
            type="submit"
            variant="primary"
          >
            {t("forms.actions.send")}
          </Button>,
        ]}
      />
      <input
        type="hidden"
        name="id"
        ref={register()}
        defaultValue={mad.id}
      />
      <MadConfigurationForm
        defaultValues={mad}
        {...{ register, reset, ...useFormMethods }}
      />
    </form>
  );
};

type EditMadScreenProps = RouteComponentProps<{ id: string }>;

const EditMadScreen: React.FC<EditMadScreenProps> = ({ match }) => {
  const t = useTranslations();

  const { data, loading, error } = useGetMadFull({
    variables: { id: match.params.id },
  });

  const mad = data?.mad;

  return (
    <ScreenContainer
      loading={loading}
      error={
        !mad?.canInsert ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      {mad && <EditMadScreenForm mad={mad} />}
    </ScreenContainer>
  );
}

export default EditMadScreen;
